import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import DialogHeader from "./DialogHeader";
import { create_UUID, getTranslation } from "common";
import { useSelectedOrganization, useTranslations } from "hooks";
// Dal percorso diretto: il barrel `hooks` non lo esporta, e il dialog del task
// lo importa così.
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { controlsFor } from "components/custom-fields/controlKinds";
import {
  Add,
  Delete,
  DragIndicator,
  Settings,
  ViewAgenda,
  ViewAgendaOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import type { FieldDefinition, FieldType } from "model/CustomFields";
import type { LegacySchemaRow } from "components/custom-fields/legacyAdapter";
import {
  FIELD_GROUPS,
  defaultConfigFor,
  groupLabelKey,
  newField,
  typeLabelKey,
} from "components/custom-fields/fieldTypes";
import {
  addField,
  hasUnnamedTypedRow,
  isDirty,
  moveFieldTo,
  removeField,
  updateField,
} from "components/custom-fields/schemaDraft";
import { validateFieldConfig } from "components/custom-fields/fieldConfig";
import { translateMessage } from "components/custom-fields/messages";
import { apiError, customFieldsApi } from "api/customFieldsApi";
import FieldConfigurationDialog from "components/custom-fields/FieldConfigurationDialog";
import FieldVisibilityDialog from "components/custom-fields/FieldVisibilityDialog";
import type { ElementChoice } from "components/custom-fields/visibilityImpact";
import TypeIcon from "components/custom-fields/typeIcon";

/**
 * Quel tipo ha qualcosa da configurare?
 *
 * `unknown` conta come `resolved`: un parametro dichiarato in `FIELD_CONTROLS`
 * e senza specie è un difetto che il configuratore mostra — nascondendogli il
 * pulsante lo si renderebbe invisibile invece che visibile.
 *
 * L'allegato non ha parametri nel contratto, ma il suo configuratore ha una
 * cosa da fare: il nome del campo.
 */
const isConfigurable = (type: FieldType): boolean => {
  if (type === "attachment") return true;
  const { resolved, unknown } = controlsFor(type);
  return resolved.length + unknown.length > 0;
};

/**
 * La riga legacy, riesportata qui per i consumatori che la importavano da questo
 * file. Vive nell'adapter, accanto alle due conversioni che la riguardano.
 *
 * @deprecated importare `LegacySchemaRow` da `components/custom-fields/legacyAdapter`.
 */
export type Data = LegacySchemaRow;

interface ProcessModelSchemaProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: (fields: FieldDefinition[]) => Promise<void>;
  schema: FieldDefinition[];
  /** Le corsie del modello, per il selettore di visibilità. */
  lanes?: readonly ElementChoice[];
  /** I task del modello. */
  tasks?: readonly ElementChoice[];
  /**
   * L'organizzazione, per i registri **locali** del modello.
   *
   * Assente significa che il chiamante non li amministra da qui: la sezione non
   * si mostra, invece di mostrarsi vuota.
   */
  organizationId?: string;
  /**
   * L'id del modello, per validare le formule contro il server.
   *
   * Assente in creazione: il modello non è ancora salvato, e una formula la cui
   * sintassi la dice solo il server non si può dichiarare valida in locale.
   */
  modelId?: string;
}

/**
 * La master dei campi.
 *
 * Lavora su **definizioni del contratto**, non sulla riga legacy
 * `{type,name,id,inCard}`: quella conosce quattro tipi e non ha configurazione,
 * visibilità né obbligatorietà. La conversione avviene ai confini — chi apre la
 * master legge legacy e proietta, chi salva riproietta.
 *
 * La bozza è isolata: i helper di `schemaDraft` restituiscono array nuovi e non
 * mutano mai l'originale, quindi annullare è smettere di guardare la copia e non
 * un ripristino da riconquistare.
 */
const ProcessModelSchema: React.FC<ProcessModelSchemaProps> = ({
  open,
  setOpen,
  onSave,
  schema,
  lanes = [],
  tasks = [],
  modelId,
  organizationId,
}) => {
  const translations = useTranslations();
  const navigate = useNavigate();
  const selectedOrganization = useSelectedOrganization();
  /**
   * La subscription, per lo spazio degli allegati.
   *
   * Lo stesso hook del dialog del task (`enabled: open`): chiederlo a dialog
   * chiusa sarebbe una chiamata per una schermata che non si sta guardando.
   */
  const { storageDiskCanUse, bypassSubscription } = useSubscriptionFeatures({
    enabled: open,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [askDiscard, setAskDiscard] = useState(false);
  /** Il campo di cui si sta scegliendo la visibilità, o `null`. */
  const [scoping, setScoping] = useState<FieldDefinition | null>(null);
  /**
   * Il campo in configurazione, o `null`.
   *
   * La master possiede la bozza, quindi possiede anche l'editor di una sua
   * riga: passare il compito al genitore lo obbligherebbe a entrare in uno
   * stato che non e' suo, e a riscriverlo da fuori.
   */
  const [configuring, setConfiguring] = useState<FieldDefinition | null>(null);
  /**
   * Quante istanze di processo attive ha questo modello, o `null` se non si sa.
   *
   * `null` è **ignoto**, e ignoto non blocca: vedi l'effetto qui sotto.
   */
  const [activeInstanceCount, setActiveInstanceCount] = useState<number | null>(null);

  /** La riga vuota in coda è il segnaposto per il campo successivo. */
  const emptyRow = () => newField("text", schema.length, "");
  const [rows, setRows] = useState<FieldDefinition[]>([...schema]);

  /**
   * C'è un campo allegato, e lo spazio della subscription non c'è.
   *
   * Alla progettazione non ci sono file da pesare: l'unica cosa che si può dire
   * è che lo spazio è finito. Dirlo qui costa una riga; scoprirlo al primo
   * caricamento costa un documento che non si allega. Le condizioni sono quelle
   * del dialog del task (`storageDiskCanUse`, `bypassSubscription`), perché la
   * risorsa è la stessa.
   */
  const attachmentWithoutStorage =
    !bypassSubscription &&
    storageDiskCanUse === false &&
    rows.some((row) => row.type === "attachment");

  useEffect(() => {
    if (!open) {
      setRows([...schema]);
      setUnnamedTypedShown(false);
    }
    // La bozza si ricostruisce solo alla chiusura: rigenerarla a ogni apertura
    // scarterebbe una modifica in corso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schema]);

  /**
   * Quante istanze attive ha il modello, lette dall'analisi d'impatto.
   *
   * È l'unico posto del client che conosce questo conteggio, e `analyzeSchema`
   * **non memorizza** nulla: leggerlo qui non ha effetti collaterali. Se il
   * conteggio resta **ignoto** — modello non ancora salvato, chiamata fallita —
   * si sceglie di **non** bloccare: un blocco su un dato incerto è un falso
   * rifiuto, e il server permetterebbe comunque la rimozione.
   */
  useEffect(() => {
    if (!open || !modelId) {
      setActiveInstanceCount(null);
      return;
    }
    // La dialog può chiudersi mentre le due chiamate sono in volo: senza questo
    // flag si farebbe setState su un componente smontato.
    let alive = true;
    (async () => {
      try {
        const documento = await customFieldsApi.getSchema(String(modelId));
        const analisi = await customFieldsApi.analyzeSchema(String(modelId), {
          operationId: create_UUID(),
          // Le revisioni su cui la lettura è costruita: dichiararle sbagliate
          // produrrebbe un conflitto che qui non interessa, ma resta corretto.
          expectedRevision: {
            schema: documento.schemaRevision,
            policy: documento.policyRevision,
          },
          schema: documento,
          reconciliation: "next_save_or_transition",
        });
        if (alive) setActiveInstanceCount(analisi.counts?.activeInstances ?? 0);
      } catch {
        // Fail-open: conteggio ignoto → la ✕ resta abilitata.
        if (alive) setActiveInstanceCount(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, modelId]);

  // Il blocco scatta solo con un conteggio **noto e positivo**.
  const instancesBlock = (activeInstanceCount ?? 0) > 0;

  // Una riga nuova — il segnaposto in coda o una aggiunta con «aggiungi campo» —
  // non esiste ancora nello schema salvato: modificarla non tocca le istanze già
  // avviate, quindi resta esente dal blocco.
  const isNewRow = (id: string) => !schema.some((f) => f.fieldId === id);

  /**
   * Il titolo del tooltip della ✕ bloccata.
   *
   * `getTranslation` restituisce la **chiave** quando la voce manca dal
   * catalogo: riconoscerla e ripiegare sull'italiano evita di leggere a schermo
   * `custom_fields.field.delete.blocked_active_instances`.
   */
  const deleteBlockedTitle = ((): string => {
    const chiave = "custom_fields.field.delete.blocked_active_instances";
    const risolta = getTranslation(translations, chiave);
    const template =
      !risolta || risolta === chiave
        ? "Il modello ha {N} istanze attive: i campi non si possono rimuovere."
        : risolta;
    return translateMessage(template, { N: activeInstanceCount ?? 0 });
  })();

  /** Il titolo del tooltip dell'ingranaggio bloccato (stesso fallback della ✕). */
  const configureBlockedTitle = ((): string => {
    const chiave = "custom_fields.field.configure.blocked_active_instances";
    const risolta = getTranslation(translations, chiave);
    const template =
      !risolta || risolta === chiave
        ? "Il modello ha {N} istanze attive: terminane o cancellane una prima di configurare i campi."
        : risolta;
    return translateMessage(template, { N: activeInstanceCount ?? 0 });
  })();

  // Un campo esiste quando ha una label: il segnaposto non è un campo, e
  // confrontarlo con lo schema direbbe "modificato" a ogni apertura.
  const namedRows = useMemo(() => rows.filter((row) => row.label.trim() !== ""), [rows]);
  const dirty = useMemo(() => isDirty(namedRows, schema), [namedRows, schema]);
  const duplicateNames = useMemo(() => {
    const names = namedRows.map((row) => row.label.trim());
    return new Set(names.filter((name, index) => names.indexOf(name) !== index));
  }, [namedRows]);
  const duplicateNameKey = "custom_fields.error.duplicate_field_name";
  const duplicateNameTranslation = getTranslation(translations, duplicateNameKey);
  const duplicateNameMessage = !duplicateNameTranslation || duplicateNameTranslation === duplicateNameKey
    ? "Esiste già un campo con questo nome."
    : duplicateNameTranslation;

  // Una riga a label vuota non viene salvata (vedi `namedRows`): un tipo scelto
  // lì è un intento che andrebbe perso senza avviso.
  const hasUnnamedTyped = hasUnnamedTypedRow(rows);

  /**
   * Gli errori di configurazione per riga: chiavi di traduzione, non testo.
   *
   * Servono a rispondere alla domanda che il rifiuto del server lasciava
   * aperta — *quale* riga — e si svuotano appena la riga viene toccata, perché
   * un errore che resta dopo la correzione è peggio di nessun errore.
   */
  const [rowIssues, setRowIssues] = useState<Record<string, string[]>>({});
  const issuesFor = (fieldId: string) => rowIssues[fieldId] ?? [];

  /**
   * Se l'avviso «tipo senza nome» è stato mostrato.
   *
   * L'errore è differito al salvataggio: farlo comparire appena si sceglie il
   * tipo bloccherebbe una riga a metà compilazione, prima che l'utente abbia
   * avuto modo di darle un nome. Mostrarlo solo al tentativo di salvare lo rende
   * un avviso sul proprio gesto, non un ostacolo mentre si scrive.
   */
  const [unnamedTypedShown, setUnnamedTypedShown] = useState(false);

  /**
   * Il testo sotto la riga: una diagnosi sola, ripetuta sotto **nome e tipo**.
   *
   * Il rosso dice quale riga, ma un messaggio sotto un solo campo lascia l'altro
   * piu' corto e la riga sembra storta. Lo stesso testo sotto entrambi tiene
   * allineate le due input e dice cosa correggere.
   */
  const rowMessageFor = (row: FieldDefinition): string => {
    if (duplicateNames.has(row.label.trim())) return duplicateNameMessage;
    if (unnamedTypedShown && row.label.trim() === "" && row.type !== "text") {
      const key = "custom_fields.error.type_without_name";
      const risolta = getTranslation(translations, key);
      return !risolta || risolta === key
        ? "Un tipo scelto senza nome non verrebbe salvato."
        : risolta;
    }
    const issue = issuesFor(row.fieldId)[0];
    if (!issue) return "";
    const risolta = getTranslation(translations, issue);
    return !risolta || risolta === issue ? "Configurazione non valida." : risolta;
  };

  const update = (fieldId: string, changes: Partial<FieldDefinition>) => {
    // Un errore che resta dopo la correzione è peggio di nessun errore: toccare
    // la riga rimette l'avviso in attesa del prossimo salvataggio.
    setUnnamedTypedShown(false);
    setRowIssues((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
    setRows((prev) => updateField(prev, fieldId, changes));
  };

  /**
   * Chiudere è una decisione, non un effetto collaterale.
   *
   * Con una bozza modificata si chiede prima: chiudere per sbaglio e perdere il
   * lavoro è l'unica cosa che questo dialog non può rimediare dopo.
   */
  const requestClose = () => {
    if (dirty) {
      setAskDiscard(true);
      return;
    }
    setOpen(false);
  };

  const confirmDiscard = () => {
    setAskDiscard(false);
    setRows([...schema]);
    setOpen(false);
  };

  const handleSave = async () => {
    if (duplicateNames.size > 0 || loading) return;
    // L'avviso si rivela solo al tentativo di salvare: la riga a metà resta
    // utilizzabile finché l'utente non prova a chiudere i conti.
    if (hasUnnamedTyped) {
      setUnnamedTypedShown(true);
      return;
    }
    // Le configurazioni incomplete si fermano qui: un `autoincrement` senza
    // progressivo associato non deve nemmeno partire, e la riga si marca come
    // quando il rifiuto arriva dal server.
    const locali: Record<string, string[]> = {};
    for (const row of namedRows) {
      const issues = validateFieldConfig(row.type, row.config);
      if (issues.length > 0) locali[row.fieldId] = issues;
    }
    if (Object.keys(locali).length > 0) {
      setRowIssues(locali);
      return;
    }
    try {
      setLoading(true);
      await onSave(namedRows.map((row, index) => ({ ...row, position: index })));
      setRowIssues({});
      setOpen(false);
    } catch (e) {
      // Il rifiuto del server **nomina** le righe (`fieldErrors`), e ora quelle
      // righe si marcano. Prima questa riga diceva solo `console.error` e
      // nient'altro: il dialog restava aperto, la snackbar diceva «correggi i
      // valori indicati», e **nessuna riga era indicata** — l'unica via
      // d'uscita era indovinare fra tutti i campi.
      //
      // Il messaggio resta quello che il chiamante ha già mostrato: marcare è
      // in più, non al posto di.
      const dalServer: Record<string, string[]> = {};
      for (const item of apiError(e)?.fieldErrors ?? []) {
        if (!item?.fieldId) continue;
        dalServer[item.fieldId] = [...(dalServer[item.fieldId] ?? []), item.messageKey];
      }
      setRowIssues(dalServer);
      console.error(String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      id="process-model-schema-dialog"
      maxWidth="md"
      fullWidth
      open={open}
      onClose={requestClose}
    >
      <DialogHeader sx={{ px: 6 }} onClose={requestClose}>
        {getTranslation(translations, "process_model.schema.title")}
      </DialogHeader>
      <DialogTitle sx={{ pl: 6, py: 0 }} variant="subtitle2">
        {getTranslation(translations, "process_model.schema.description")}
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} pt={2} flexDirection={"column"}>
          {/* Lo spazio degli allegati è finito, e c'è un campo che ne consuma:
              stessa frase e stessa azione del dialog del task, perché la
              risorsa è la stessa. Non impedisce di progettare — il modello può
              essere pubblicato dopo l'upgrade. */}
          {attachmentWithoutStorage && (
            <Alert
              severity="error"
              data-testid="cf-master-storage"
              sx={{ py: 0.5 }}
              action={
                <Button
                  size="small"
                  onClick={() =>
                    navigate(
                      `/dashboard/organizations/${organizationId}/billing`,
                      { state: { organizationName: selectedOrganization?.name } }
                    )
                  }
                >
                  {getTranslation(translations, "generic.upgrade")}
                </Button>
              }
            >
              {getTranslation(translations, "storage.limit.reached")}
            </Alert>
          )}
          {/* Le righe sono un'area di rilascio a sé: il segnaposto che rbd
              infila durante il trascinamento muove solo loro, e i pulsanti in
              fondo (registri, aggiungi) restano dove sono. */}
          <DragDropContext
            onDragEnd={({ draggableId, source, destination }) => {
              // La riga si cerca per `draggableId` — che è il `fieldId` — e non
              // per indice: nel frattempo l'indice è cambiato sotto.
              if (destination && destination.index !== source.index) {
                setRows((prev) =>
                  moveFieldTo(prev, draggableId, destination.index)
                );
              }
            }}
          >
            <Droppable droppableId="cf-schema-fields">
              {(provided) => (
                <Stack
                  gap={3}
                  flexDirection={"column"}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {rows.map((row, idx) => (
                    <Draggable
                      key={row.fieldId}
                      draggableId={row.fieldId}
                      index={idx}
                    >
                      {(drag) => (
                        <Stack
                          component="form"
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          data-testid={`cf-row-${row.fieldId}`}
                          spacing={0.5}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                          {/* La maniglia è l'unico punto che trascina: senza,
                              un clic dentro un campo di testo sposterebbe la
                              riga mentre si scrive. */}
                          <Box
                            {...drag.dragHandleProps}
                            data-testid={`cf-handle-${row.fieldId}`}
                            sx={{
                              display: "flex",
                              cursor: "grab",
                              color: "text.disabled",
                              "&:hover": { color: "text.secondary" },
                              "&:active": { cursor: "grabbing" },
                            }}
                          >
                            <DragIndicator fontSize="small" />
                          </Box>
                          <Tooltip
                            // Il titolo vuoto non è un fumetto vuoto: MUI non
                            // apre il tooltip se il titolo è falsy.
                            title={
                              duplicateNames.has(row.label.trim()) ? duplicateNameMessage : ""
                            }
                          >
                            <TextField
                              size="small"
                              sx={{ flex: 1 }}
                              // Con un progressivo associato il nome non si
                              // tocca più: quello locale ricava il proprio
                              // codice da qui, e rinominare il campo lo
                              // lascerebbe con un'identità che non gli
                              // somiglia.
                              disabled={
                                row.type === "autoincrement" &&
                                Boolean(row.config?.registryId)
                              }
                              inputProps={{ "data-testid": `cf-name-${row.fieldId}` }}
                              // Qui **non** si blocca con istanze attive, a
                              // differenza del tipo e della configurazione: i valori
                              // delle istanze sono legati all'id del campo, non al
                              // suo nome, quindi rinominarlo non tocca niente di
                              // già scritto.
                              // La riga si marca **qui** perché il nome c'è su ogni
                              // riga. Il difetto è di configurazione e il
                              // configuratore lo dettaglia (`cf-config-problems`),
                              // ma finché nessuna riga era marcata non si sapeva
                              // quale aprire: il messaggio diceva «correggi i
                              // valori indicati» e non indicava niente.
                              //
                              // Il rosso dice **quale** riga, e basta: il testo sotto
                              // l'input non lo leggeva nessuno. Il nome duplicato —
                              // l'unica diagnosi che non ha altri posti — resta a un
                              // passaggio di mouse nel tooltip.
                              error={
                                duplicateNames.has(row.label.trim()) ||
                                (unnamedTypedShown && row.label.trim() === "" && row.type !== "text") ||
                                issuesFor(row.fieldId).length > 0
                              }
                              label={getTranslation(
                                translations,
                                "process_model.schema.field.name"
                              )}
                              value={row.label}
                              onKeyUp={(e) => e.stopPropagation()}
                              onChange={(e) => update(row.fieldId, { label: e.target.value })}
                            />
                          </Tooltip>
                          <TextField
                            size="small"
                            sx={{ flex: 1 }}
                            // `SelectProps` e non `inputProps`: su una `select` di MUI il
                            // secondo finisce sull'input nativo, che e' `aria-hidden` e non
                            // si puo' cliccare. Il primo sta sul combobox, che e' l'elemento
                            // che apre l'elenco.
                            SelectProps={
                              { "data-testid": `cf-type-${row.fieldId}` } as never
                            }
                            disabled={instancesBlock && !isNewRow(row.fieldId)}
                            label={getTranslation(
                              translations,
                              "process_model.schema.field.type"
                            )}
                            select
                            value={row.type}
                            onKeyUp={(e) => e.stopPropagation()}
                            onChange={({ target }) => {
                              const type = target.value as FieldType;
                              // Cambiare tipo **sostituisce** la configurazione.
                              // I parametri di un tipo non sono quelli di un
                              // altro, e ogni DTO vieta le proprietà che non
                              // conosce: conservandola, una riga cambiata da
                              // `text` a `number` portava `minLength` su un
                              // campo numerico e il server rifiutava **l'intero**
                              // schema, una proprietà per riga cambiata. Era il
                              // motivo per cui «Salva» non salvava.
                              if (type === row.type) return;
                              update(row.fieldId, {
                                type,
                                config: defaultConfigFor(type),
                              });
                            }}
                          >
                            {FIELD_GROUPS.map((group) => [
                              // I gruppi sono quelli del catalogo: quindici voci di fila
                              // non si trovano.
                              <ListSubheader key={`group-${group.key}`}>
                                {getTranslation(translations, groupLabelKey(group.key))}
                              </ListSubheader>,
                              ...group.types.map((typeName) => (
                                <MenuItem key={typeName} value={typeName}>
                                  {/* L'icona del tipo: MUI mostra la voce scelta
                                      ridisegnando questo stesso contenuto, quindi compare
                                      anche a tendina chiusa. */}
                                  <TypeIcon type={typeName as FieldType} />
                                  {getTranslation(translations, typeLabelKey(typeName as FieldType))}
                                </MenuItem>
                              )),
                            ])}
                          </TextField>
                          <Stack direction="row" alignItems="center">
                            {/* Un tipo senza parametri non ha niente da configurare:
                                la dialog si aprirebbe vuota, e un pulsante che
                                non porta da nessuna parte è peggio di un
                                pulsante che non c'è. `attachment` fa eccezione:
                                niente parametri, ma il nome sì. */}
                            {isConfigurable(row.type as FieldType) && (
                              <Tooltip
                                title={
                                  instancesBlock && !isNewRow(row.fieldId)
                                    ? configureBlockedTitle
                                    : getTranslation(translations, "custom_fields.action.configure")
                                }
                              >
                                <span>
                                  {/* Ingranaggio **e** la parola, come nel riferimento: il
                                      solo simbolo lasciava indovinare cosa si configurasse. */}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Settings fontSize="small" />}
                                    data-testid={`cf-configure-${row.fieldId}`}
                                    disabled={row.label.trim() === "" || (instancesBlock && !isNewRow(row.fieldId))}
                                    onClick={() => setConfiguring(row)}
                                    sx={{ borderRadius: 5, textTransform: "none", whiteSpace: "nowrap" }}
                                  >
                                    {getTranslation(translations, "custom_fields.action.configure")}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={translateMessage(
                                getTranslation(
                                  translations,
                                  "custom_fields.visibility.title"
                                ),
                                { field: row.label }
                              )}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  data-testid={`cf-visibility-${row.fieldId}`}
                                  disabled={row.label.trim() === ""}
                                  onClick={() => setScoping(row)}
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={getTranslation(translations, "generic.showingincard.caption")}
                            >
                              {/* Icona invece di una Checkbox: "in card" e' un
                                  interruttore, non un flag da spuntare in un
                                  modulo; l'icona piena/vuota
                                  (ViewAgenda/ViewAgendaOutlined) fa leggere lo
                                  stato senza legenda. `aria-pressed` tiene lo
                                  stato verificabile e accessibile. */}
                              <IconButton
                                size="small"
                                data-testid={`cf-incard-${row.fieldId}`}
                                aria-pressed={Boolean(row.inCard)}
                                sx={{ color: row.inCard ? "primary.main" : "text.disabled" }}
                                onClick={() => update(row.fieldId, { inCard: !row.inCard })}
                              >
                                {row.inCard ? (
                                  <ViewAgenda fontSize="small" />
                                ) : (
                                  <ViewAgendaOutlined fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            {/* Il tooltip si mostra **solo** quando il blocco è
                                attivo: altrimenti il titolo sarebbe vuoto e MUI
                                mostrerebbe un fumetto vuoto. Il wrapper `<span>`

                                serve perché un bottone disabilitato non emette
                                eventi hover (stesso pattern della ✕ di
                                visibilità qui sopra). */}
                            <Tooltip title={instancesBlock ? deleteBlockedTitle : ""}>
                              <span>
                                <IconButton
                                  size="small"
                                  data-testid={`cf-delete-${row.fieldId}`}
                                  disabled={row.label.trim() === "" || instancesBlock}
                                  sx={{ opacity: row.label ? 1 : 0 }}
                                  onClick={() => setRows((prev) => removeField(prev, row.fieldId))}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                          </Stack>
                          {rowMessageFor(row) && (
                            // Il testo parte dove parte la input del nome:
                            // maniglia (20px) + spazio della riga (8px).
                            <Typography variant="caption" color="error" sx={{ pl: "28px" }}>
                              {rowMessageFor(row)}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
          {/* Nessun pulsante per i registri locali: il catalogo non ha una
              voce per il loro nome (`custom_fields.sequence.local` vale
              «Modello»), e l'unica etichetta che la master mostrava era quella.
              Il campo `autoincrement` li crea e li corregge dal suo
              configuratore, che è il punto in cui servono. */}
          <FlowentiButton
            // Aggiungere è possibile solo quando la riga aperta ha una label:
            // due segnaposto insieme non hanno senso.
            disabled={rows.some((row) => row.label.trim() === "")}
            onClick={() => setRows((prev) => addField(prev, emptyRow()))}
            startIcon={<Add />}
            id="add-userSchema"
            data-testid="cf-master-add"
            size="small"
            variant="outlined"
            // `FlowentiButton` impone `px: 3, py: 2` a ogni variante: su una
            // riga sola diventava il pulsante più alto del dialogo, che è
            // l'opposto della sua importanza.
            // `alignSelf` perché in una colonna flex i figli si allargano a
            // tutta la larghezza: senza, il pulsante diventava una barra da un
            // capo all'altro del dialogo — più imponente dell'elenco che serve.
            // Gli altri «aggiungi» del progetto (`cf-option-add`) fanno lo stesso.
            sx={{ px: 2, py: 0.75, alignSelf: "flex-start" }}
          >
            {getTranslation(translations, "process_model.schema.add_field")}
          </FlowentiButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose} data-testid="cf-master-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <LoadingButton
          loading={loading}
          disabled={loading || duplicateNames.size > 0}
          onClick={handleSave}
          id="save-btn"
          data-testid="cf-master-save"
        >
          {getTranslation(translations, "pm.button.save")}
        </LoadingButton>
      </DialogActions>
      <FieldConfigurationDialog
        open={configuring !== null}
        field={configuring}
        modelId={modelId}
        // I campi scritti finora, e non il segnaposto di coda: sono quelli che
        // una formula può riferire.
        fields={namedRows}
        onClose={() => setConfiguring(null)}
        // Il risultato torna nella bozza, non nello schema: salvare la
        // configurazione non e' salvare il modello, ed e' il pulsante della
        // master a decidere quando.
        onSave={(updated) => {
          // L'errore della riga si toglie appena la configurazione è chiusa:
          // come toccare nome o tipo, salvarla è una correzione, e un avviso
          // che resta dopo la correzione è peggio di nessun avviso.
          setRowIssues((prev) => {
            if (!(updated.fieldId in prev)) return prev;
            const next = { ...prev };
            delete next[updated.fieldId];
            return next;
          });
          setRows((prev) => updateField(prev, updated.fieldId, updated));
          setConfiguring(null);
        }}
      />
      <FieldVisibilityDialog
        open={scoping !== null}
        field={scoping}
        lanes={lanes}
        tasks={tasks}
        onClose={() => setScoping(null)}
        onSave={(visibility) => {
          // Stesso confine del configuratore: nella bozza, non nello schema.
          if (scoping) {
            setRows((prev) => updateField(prev, scoping.fieldId, { visibility }));
          }
          setScoping(null);
        }}
      />
      <Dialog
        open={askDiscard}
        onClose={() => setAskDiscard(false)}
        data-testid="cf-discard-dialog"
      >
        <DialogTitle>
          {getTranslation(translations, "custom_fields.master.unsaved_message")}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setAskDiscard(false)}>
            {getTranslation(translations, "custom_fields.action.keep_editing")}
          </Button>
          <Button
            onClick={confirmDiscard}
            color="error"
            data-testid="cf-discard-confirm"
          >
            {getTranslation(translations, "custom_fields.action.discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProcessModelSchema;
